import apiClient, { publicApiClient } from "./apiClient";
import { AdminSession, AuthenticatorSetup, AuthenticatorStatus, AuthorizationContext, LoginChallenge } from "../types/auth";

export async function login(credentials: { email: string; password: string }): Promise<LoginChallenge> {
  const { data } = await publicApiClient.post<LoginChallenge>("auth/login", { ...credentials, email: credentials.email.trim().toLowerCase() });
  if (data.requires2FA !== true || !data.tempToken || typeof data.setupRequired !== "boolean" || !(data.expiresIn > 0)) {
    throw new Error("Unable to start authenticator verification. Please try signing in again.");
  }
  return data;
}
const challengeHeaders = (token: string) => ({ Authorization: `Bearer ${token}` });
export async function setupAuthenticator(token: string): Promise<AuthenticatorSetup> {
  const { data } = await publicApiClient.post<AuthenticatorSetup>("auth/2fa/setup", {}, { headers: challengeHeaders(token) });
  if (!data.manualKey || !data.qrCodeDataUrl?.startsWith("data:image/")) throw new Error("Unable to load authenticator setup. Please retry.");
  return data;
}
export async function verifyAuthenticator(token: string, code: string, setup: boolean): Promise<AdminSession> {
  const { data } = await publicApiClient.post<AdminSession>(`auth/2fa/${setup ? "verify-setup" : "verify-login"}`, { code }, { headers: challengeHeaders(token) });
  if (!data.accessToken || !data.refreshToken || !data.admin?.id) throw new Error("Unable to complete sign in. Please sign in again.");
  return data;
}
export async function getAuthorization(signal?: AbortSignal): Promise<AuthorizationContext> {
  const { data } = await apiClient.get<AuthorizationContext>("rbac/me", { signal });
  if (typeof data.isSuperAdmin !== "boolean" || !Array.isArray(data.permissionKeys)) throw new Error("Unable to load your access permissions.");
  return data;
}
export async function logoutAdmin() { await apiClient.post("auth/logout"); }
export async function getAuthenticatorStatus(): Promise<AuthenticatorStatus> {
  return (await apiClient.get<AuthenticatorStatus>("auth/2fa/status")).data;
}
export async function changeAdminPassword(body: { currentPassword: string; newPassword: string; confirmNewPassword: string }) {
  return (await apiClient.post<{ accessToken: string; refreshToken: string; message: string }>("auth/change-password", body)).data;
}

// Frontend naming aliases share the isolated challenge client.
export { setupAuthenticator as setupTwoFactor, verifyAuthenticator as verifyTwoFactor, getAuthenticatorStatus as getTwoFactorStatus };
export type { AdminSession as AuthSession, LoginChallenge as TwoFactorChallenge, AuthenticatorSetup as TwoFactorSetup } from "../types/auth";
